import type { ApiClientErrorCode, Partner } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { PartnerContactButtons } from './PartnerContactButtons';

/**
 * Presenter Экрана 2.8.3 «Карточка партнёра» (Блок 8, ADR-0080, Фаза D3).
 *
 * Разрежённая детальная карточка (как декор/фурнитура): рисуем ТОЛЬКО заполненные
 * поля; `null`-поля (`address`/`description`/`workingHours`/`photoUrl`) не рендерятся.
 * Порядок (прототип `ScreenCard`): фото (если есть) → название → категория →
 * подкатегории `subcategories[]` (ADR-0097: 1..3 у «Распил…», иначе пусто → не
 * рисуем) → город/адрес/описание/часы → кнопки «Написать»
 * (PartnerContactButtons) → кнопка «Отзывы».
 *
 * P-031: ТЕЛЕФОН НЕ выводим как текст/ссылку — анти-парсинг (номер не в статичном
 * DOM). Звонок — через кнопку «Позвонить» в `PartnerContactButtons` (строит `tel:`
 * через `buildCallLink` ПРИ КЛИКЕ). Прототип с `<a href="tel:+…">` — до-P-031
 * артефакт (хардненинг Aibek 2026-06-10, позже прототипа).
 *
 * 5 состояний (P-021): idle / loading / success / empty (404 «Партнёр не найден») /
 * error. Дизайн-система: bg-canvas/bg-card (P-038), text-ink (P-039), Manrope (P-043),
 * палитра токенами (P-045), жёлтый — заливка, не текст (P-041). Layout shell P-046,
 * вертикальные элементы P-019, без языкопереключателя (P-020). VideoButton тут НЕ
 * дублируется — это дочерний экран (entry — 2.8.1 PartnersCategories), P-040.
 * «Назад» — история (BottomBar/Header, P-036).
 */
export type PartnerCardState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface PartnerCardViewProps {
  state: PartnerCardState;
  partner?: Partner;
  /**
   * Код/сообщение ошибки. Намеренно НЕ рендерится (фиксированный текст ErrorState;
   * серверный message пользователю не показываем — P-009). Оставлен для паритета и
   * возможной телеметрии.
   */
  error?: { code: ApiClientErrorCode; message: string };
  /** «Отзывы» → /partners/:id/reviews (Container навигирует). */
  onOpenReviews: () => void;
  /** «Назад» для empty/error (на список/предыдущий экран, P-036). */
  onBack: () => void;
}

export function PartnerCardView(props: PartnerCardViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Партнёр" />
      <section className="flex-1 p-6 overflow-y-auto">
        {props.state === 'idle' && <Centered text="Загрузка партнёра…" tone="muted" />}
        {props.state === 'loading' && <Centered text="Загружаем…" tone="muted" />}
        {props.state === 'success' && props.partner && (
          <SuccessState partner={props.partner} onOpenReviews={props.onOpenReviews} />
        )}
        {props.state === 'empty' && <NotFoundState onBack={props.onBack} />}
        {props.state === 'error' && <ErrorState onBack={props.onBack} />}
      </section>
      <BottomBar />
    </main>
  );
}

const REVIEWS_BTN_CLASS = [
  'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-header text-ink',
  'font-semibold text-base text-left py-3 px-4 flex items-center gap-3',
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
].join(' ');

function SuccessState({ partner, onOpenReviews }: { partner: Partner; onOpenReviews: () => void }) {
  // Категория (verbatim, P-001). ADR-0097: подкатегории подрядчика — список
  // `subcategories[]`; рисуем их отдельным полем ниже, только если массив непуст
  // (в текущих данных подкатегории есть лишь у «Распил…»). Verbatim (P-001).
  const categoryLine = partner.category;

  return (
    <div className="flex flex-col gap-5">
      {/* Фото — только если есть (signed URL из контракта). Разрежённо: null не рисуем. */}
      {partner.photoUrl && (
        <div className="w-full aspect-video rounded-xl border-[1.5px] border-accent overflow-hidden bg-card">
          <img src={partner.photoUrl} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      <article className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-5 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="font-display text-h2 text-ink m-0">{partner.name}</h2>
          <p className="text-muted text-base m-0">{categoryLine}</p>
        </div>

        {/* Разрежённые поля — только заполненные (null НЕ рисуем). Телефон НЕ выводим
            текстом (P-031) — звонок через кнопку «Позвонить» ниже. */}
        <dl className="flex flex-col gap-2 m-0">
          {/* Подкатегории (ADR-0097) — только если непусто (разрежённо, как null-поля).
              Неинтерактивно, verbatim через join (P-001). */}
          {partner.subcategories.length > 0 && (
            <Field label="Подкатегории" value={partner.subcategories.join(', ')} />
          )}
          <Field label="Город" value={partner.city} />
          {partner.address && <Field label="Адрес" value={partner.address} />}
          {partner.workingHours && <Field label="Часы работы" value={partner.workingHours} />}
          {partner.description && <Field label="Описание" value={partner.description} />}
        </dl>

        {/* Кнопки «Написать» (WA/TG/MAX/Позвонить) — P-031, вертикально (P-019). */}
        <PartnerContactButtons partner={partner} />

        {/* «Отзывы» → саб-экран (empty в D3; реальные отзывы — Фаза E). */}
        <button
          type="button"
          onClick={onOpenReviews}
          aria-label="Отзывы"
          className={REVIEWS_BTN_CLASS}
        >
          <span className="flex-1">Отзывы</span>
          <span aria-hidden="true" className="text-muted">
            ›
          </span>
        </button>
      </article>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-muted text-sm">{label}</dt>
      <dd className="text-ink text-base m-0">{value}</dd>
    </div>
  );
}

function Centered({ text, tone }: { text: string; tone: 'muted' | 'error' }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p
        className={[
          tone === 'error' ? 'text-error' : 'text-muted',
          'text-[18px] max-w-[280px] m-0 leading-normal',
        ].join(' ')}
      >
        {text}
      </p>
    </div>
  );
}

function NotFoundState({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">Партнёр не найден</p>
      <BackButton onBack={onBack} />
    </div>
  );
}

function ErrorState({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      <BackButton onBack={onBack} />
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
    >
      Назад к списку
    </button>
  );
}
